import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera, PartyPopper, Users, Sparkles, ChevronRight, QrCode } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

/**
 * Detect Smart TV / installed-PWA / TWA wrappers so the APK installed on
 * Fire TV / Android TV / Philco automatically lands on the pairing screen
 * instead of the marketing landing page. We check (any of):
 *   - PWA standalone display-mode (real PWA install)
 *   - iOS Safari "Add to Home Screen" flag
 *   - Android TWA referrer (`android-app://...`)
 *   - Android/Fire/Smart TV strings inside the user-agent
 * If matched, the user is replaced (not pushed) to `/tv` so the back button
 * does not bring them back to the landing page.
 */
function isTvLikeClient() {
  try {
    const ua = (navigator.userAgent || "").toLowerCase();
    const tvUa = /\b(smart-?tv|googletv|android tv|androidtv|appletv|hbbtv|netcast|tizen|web0s|webos|crkey|silk|aftb|aftt|afts|aftm|aftn|firetv|fire tv|bravia)\b/.test(ua);
    const standalone = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(display-mode: standalone)").matches
      : false;
    const iosStandalone = typeof window !== "undefined" && window.navigator && window.navigator.standalone === true;
    const twaReferrer = typeof document !== "undefined" && (document.referrer || "").startsWith("android-app://");
    return tvUa || standalone || iosStandalone || twaReferrer;
  } catch {
    return false;
  }
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  // If the page is being rendered inside the TV APK / installed PWA, bounce
  // to /tv immediately. We do this once on mount, never re-runs.
  useEffect(() => {
    if (isTvLikeClient()) {
      navigate("/tv", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = (e) => {
    e.preventDefault();
    if (code.trim()) {
      navigate(`/join/${code.trim()}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7] overflow-hidden" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
              <Camera className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-xl tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Festa.IA
            </span>
          </div>
          <Link to="/login">
            <Button
              variant="outline"
              className="border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
              data-testid="login-btn"
            >
              Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#FEE2E2] text-[#1C1917] border-2 border-[#1C1917] px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider mb-8 shadow-[2px_2px_0px_#1C1917]">
            <Sparkles className="w-4 h-4" strokeWidth={2.5} />
            Powered by AI
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#1C1917] mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Todas as fotos do seu evento,{" "}
            <span className="text-[#FF6B6B]">organizadas por IA</span>
          </h1>

          <p className="text-base sm:text-lg text-[#44403C] max-w-2xl mx-auto mb-12 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Convidados enviam fotos em tempo real. Nossa IA classifica, modera e organiza tudo automaticamente em albuns inteligentes.
          </p>

          {/* Action Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto">
            {/* Host Card */}
            <div
              className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-6 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#1C1917] transition-all duration-200 cursor-pointer text-left"
              onClick={() => navigate("/register")}
              data-testid="create-event-card"
            >
              <div className="w-12 h-12 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917] mb-4">
                <PartyPopper className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-bold text-[#1C1917] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Criar Evento
              </h3>
              <p className="text-sm text-[#78716C] mb-3">Organize as fotos da sua festa automaticamente</p>
              <span className="inline-flex items-center text-[#FF6B6B] font-bold text-sm">
                Comecar <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </div>

            {/* Guest Card */}
            <div
              className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-6 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#1C1917] transition-all duration-200 text-left"
              data-testid="join-event-card"
            >
              <div className="w-12 h-12 bg-[#8B5CF6] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917] mb-4">
                <QrCode className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-bold text-[#1C1917] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Entrar no Evento
              </h3>
              <form onSubmit={handleJoin} className="flex gap-2 mt-2">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Codigo do evento"
                  className="border-2 border-[#1C1917] rounded-lg text-sm"
                  data-testid="event-code-input"
                />
                <Button
                  type="submit"
                  className="bg-[#8B5CF6] text-white border-2 border-[#1C1917] rounded-lg shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all px-4"
                  data-testid="join-event-btn"
                >
                  Ir
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 border-t-2 border-[#E7E5E4]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#1C1917] text-center mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Como funciona
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: QrCode, title: "QR Code", desc: "Convidados escaneiam e entram no evento instantaneamente", color: "#FF6B6B" },
              { icon: Camera, title: "Upload", desc: "Fotos e videos enviados direto do celular em tempo real", color: "#8B5CF6" },
              { icon: Sparkles, title: "IA Magica", desc: "Classificacao, moderacao e organizacao automatica", color: "#FF6B6B" },
            ].map((feat) => (
              <div
                key={feat.title}
                className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-6 text-center"
              >
                <div
                  className="w-14 h-14 border-2 border-[#1C1917] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]"
                  style={{ backgroundColor: feat.color }}
                >
                  <feat.icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-bold text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {feat.title}
                </h3>
                <p className="text-sm text-[#78716C]">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#E7E5E4] py-8 px-4 text-center">
        <p className="text-sm text-[#78716C]">Festa.IA - Todas as memorias, organizadas por inteligencia artificial</p>
      </footer>
    </div>
  );
}
